<form [formGroup]="form" (ngSubmit)="onSubmit()">
  <h2 mat-dialog-title>{{ "devices.edit.title" | translate }}</h2>
  <mat-dialog-content>
    @if (validTypeOptions.length > 1) {
      <div class="grid grid-cols-2 gap-2">
        <app-input-wrapper
          [label]="'devices.edit.type' | translate"
          [control]="form.controls['type']"
          isSelect
        >
          <select formControlName="type">
            @for (
              validTypeOption of validTypeOptions;
              track validTypeOption.value
            ) {
              <option [value]="validTypeOption.value">
                {{ validTypeOption.label | translate }}
              </option>
            }
          </select>
        </app-input-wrapper>
      </div>
    }

    <app-input-wrapper
      [label]="'devices.edit.name' | translate"
      [control]="form.controls['name']"
    >
      <input
        type="text"
        formControlName="name"
        [placeholder]="'devices.edit.name' | translate"
      />
    </app-input-wrapper>

    <app-input-wrapper
      [label]="'devices.edit.location.label' | translate"
      [hint]="'devices.edit.location.hint' | translate"
      [control]="form.controls['location']"
    >
      @if (hasLocationChanged && initialLocation; as location) {
        <p input-wrapper-end class="small">
          {{ "devices.edit.currentLocation" | translate }}:
          <span class="text-link" (click)="resetLocation()">
            {{ location.lat | number: "1.5-5" }},
            {{ location.lon | number: "1.5-5" }}
          </span>
        </p>
      }

      <div class="location-field">
        <div class="location-field__input-wrapper">
          <input
            class="location-field__input"
            type="text"
            [value]="
              form.value.location
                ? (form.value.location.lat | number: '1.5-5') +
                  ', ' +
                  (form.value.location.lon | number: '1.5-5')
                : ''
            "
            [placeholder]="'devices.edit.location' | translate"
            (click)="toggleMapVisibility(true)"
            readonly
          />

          <button
            type="button"
            class="button icon-button location-field__toggle"
            [class.open]="isMapVisible"
            [attr.aria-label]="
              (isMapVisible ? 'devices.edit.hideMap' : 'devices.edit.showMap')
                | translate
            "
            [title]="
              (isMapVisible ? 'devices.edit.hideMap' : 'devices.edit.showMap')
                | translate
            "
            (click)="toggleMapVisibility()"
          >
            <app-icon name="ic_map" />
          </button>
        </div>

        @if (isMapVisible) {
          <app-map
            class="location-field__map"
            [layers]="locationLayers"
            (mapClick)="onMapClick($event)"
          />
        }
      </div>
    </app-input-wrapper>

    @if (isValve) {
      <section class="mt-2">
        <h3>{{ "devices.edit.valve.headline" | translate }}</h3>
        <p class="small">{{ "devices.edit.valve.hint" | translate }}</p>

        <div class="grid grid-cols-2 gap-2">
          <app-input-wrapper
            [label]="'devices.edit.sourcePump.label' | translate"
            [control]="form.controls['sourcePumpId']"
            isSelect
          >
            <select formControlName="sourcePumpId">
              <option [ngValue]="null">
                {{ "devices.edit.sourcePump.none" | translate }}
              </option>
              @for (pump of pumps$ | async; track pump.id) {
                <option [value]="pump.id">{{ pump.name }}</option>
              }
            </select>
          </app-input-wrapper>
        </div>

        <div class="grid grid-cols-2 gap-2">
          <app-input-wrapper
            class="flex-1"
            [label]="'devices.edit.targetType' | translate"
            [control]="form.controls['deviceTargetType']"
            isSelect
          >
            <select formControlName="deviceTargetType">
              @for (
                targetTypeOption of targetTypeOptions;
                track targetTypeOption.value
              ) {
                <option [value]="targetTypeOption.value">
                  {{ targetTypeOption.label | translate }}
                </option>
              }
            </select>
          </app-input-wrapper>
        </div>

        <div class="grid grid-cols-2 gap-2">
          @if (isCisternTarget) {
            <app-input-wrapper
              [label]="'devices.edit.targetCistern' | translate"
              [control]="form.controls['targetCisternId']"
              isSelect
            >
              <select formControlName="targetCisternId">
                <option [ngValue]="null">
                  {{ "default.placeholder.select" | translate }}
                </option>
                @for (cistern of cisterns$ | async; track cistern.id) {
                  <option [value]="cistern.id">{{ cistern.name }}</option>
                }
              </select>
            </app-input-wrapper>

            <app-input-wrapper
              [label]="'devices.edit.sourcePumpThroughput.label' | translate"
              [control]="form.controls['sourcePumpThroughput']"
              [hint]="'devices.edit.sourcePumpThroughput.hint' | translate"
              unit="l/min"
            >
              <input
                type="number"
                min="0"
                formControlName="sourcePumpThroughput"
                placeholder="30,0"
              />
            </app-input-wrapper>
          }
        </div>
      </section>
    }

    <section class="mt-2">
      <h3>{{ "devices.edit.powerSource.headline" | translate }}</h3>
      <p class="small">{{ "devices.edit.powerSource.hint" | translate }}</p>
      <app-input-wrapper
        [label]="'devices.edit.powerSource.label' | translate"
        [control]="form.controls['powerSource']"
        isSelect
      >
        <select formControlName="powerSource">
          <option disabled hidden [ngValue]="null">
            {{ "devices.edit.powerSource.options.none" | translate }}
          </option>
          @for (
            powerSourceOption of powerSourceOptions;
            track powerSourceOption.value
          ) {
            <option [ngValue]="powerSourceOption.value">
              {{ powerSourceOption.label | translate }}
            </option>
          }
        </select>
      </app-input-wrapper>
      @if (hasBatteryPower) {
        <div class="flex align-start gap-2">
          <app-input-wrapper
            class="flex-1"
            [label]="
              'devices.edit.batteryVoltages.batteryMinVoltage' | translate
            "
            unit="Volt"
            [control]="form.controls['batteryMinVoltage']"
            [hint]="'devices.edit.batteryVoltages.hint' | translate"
          >
            <input
              type="number"
              min="0"
              step="0.01"
              formControlName="batteryMinVoltage"
              placeholder="2,0"
            />
          </app-input-wrapper>

          <app-input-wrapper
            class="flex-1"
            [label]="
              'devices.edit.batteryVoltages.batteryMaxVoltage' | translate
            "
            unit="Volt"
            [control]="form.controls['batteryMaxVoltage']"
          >
            <input
              type="number"
              min="0"
              step="0.01"
              formControlName="batteryMaxVoltage"
              placeholder="3,6"
            />
          </app-input-wrapper>
        </div>
      }
    </section>
  </mat-dialog-content>
  <mat-dialog-actions>
    <button type="button" class="button" mat-dialog-close>
      {{ "default.cancel" | translate }}
    </button>
    <button class="button primary-button" [disabled]="form.invalid">
      {{ "default.save" | translate }}
    </button>
  </mat-dialog-actions>
</form>
